import type { ModelOptions } from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";

/** Where the composer asks: a project, an opened conversation, or a standalone one. */
export type PickerScope = { projectId: string } | { conversationId: string } | { standalone: true };

export const modelOptionKeys = {
  all: ["model-options"] as const,
  of: (scope: PickerScope) =>
    [
      "model-options",
      "projectId" in scope
        ? `project:${scope.projectId}`
        : "conversationId" in scope
          ? `conversation:${scope.conversationId}`
          : "standalone",
    ] as const,
};

/**
 * The picker options of a scope (spec `model-choice`): the efforts the policy allows, their
 * models and implications, the default and locked state. A failed read leaves the picker out:
 * the server applies the default effort anyway.
 */
export function useModelOptions(scope: PickerScope | null, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: scope ? modelOptionKeys.of(scope) : modelOptionKeys.all,
    enabled: scope !== null && options.enabled !== false,
    staleTime: 60_000,
    // A refusal (403 standalone off, 404) is final: no retry.
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 3,
    queryFn: async (): Promise<ModelOptions> => {
      if (scope === null) {
        throw new Error("no scope");
      }
      if ("projectId" in scope) {
        return ok(
          await api.api.projects[":projectId"]["model-options"].$get({
            param: { projectId: scope.projectId },
          }),
        );
      }
      if ("conversationId" in scope) {
        return ok(
          await api.api.conversations[":conversationId"]["model-options"].$get({
            param: { conversationId: scope.conversationId },
          }),
        );
      }
      return ok(await api.api["model-options"].$get());
    },
  });
}
